import { PriceDrawerProps } from 'components/price-drawer/types'
import classNames from 'classnames'
import styles from './PriceDrawer.module.scss'
import { Button, Drawer, Icon, InlineMessage, Accordion, Heading } from '@boltenergy-be/design-system'
import { useRouter } from 'next/router'
import { formatPrice } from 'utils/format'
import { useGetLanguage } from 'hooks/useGetLanguage'
import { useTranslation } from 'next-i18next/pages'
import { getElectricityProduct, getGasProduct, setPromoOrReferralCodeInStore } from 'utils/customerFlow'
import { getPricelistFromElectricityProducts, getPricelistFromGasProducts, handleContactMeClick } from 'utils/helpers'
import { getProposition, getSimulatedPrice } from 'utils/price'
import PriceDisplay from 'features/simulation/components/proposition-card/price-display/PriceDisplay'
import { routes } from 'utils/routes'
import { selectProposition } from 'store/customer/slice'
import { useGetProductsQuery } from 'store/api/bolt-api'
import { useStoreDispatch, useStoreSelector } from 'hooks/store'
import useSelectInputGroup from 'hooks/useSelectInputGroup'
import Link from 'components/link/Link'
import { requestAndSavePropositions } from 'features/simulation/utils'
import { InputGroupKeys } from 'store/customer/enums'
import { useState } from 'react'
import { checkCanApplyPlentyPromo, getProductName } from 'utils/products'
import { CUSTOMER_TYPES, EL_PRODUCT_CODES, ELProduct, GAS_PRODUCT_CODES, NGProduct } from '@boltenergy-be/shared'
import dayjs from 'dayjs'
import { getProductPromoForDate } from 'components/promo-code/utils'

const PriceDrawer = ({ isOpen, onRequestClose, producer, product }: PriceDrawerProps) => {
  // Redux store
  const propositions = useStoreSelector((store) => store.customer.propositions)
  const { isCompany } = useSelectInputGroup(InputGroupKeys.PERSONAL_DATA)
  const { needsGas } = useSelectInputGroup(InputGroupKeys.DELIVERY_ADDRESS)
  const { selectedProducer } = useStoreSelector((store) => store.app)
  const { selectedProposition } = propositions
  const dispatch = useStoreDispatch()

  // Router
  const router = useRouter()
  const { route } = router
  const isSimulationPage = route === '/simulation'

  // RTK queries
  const { data: products } = useGetProductsQuery()

  // i18n
  const { t } = useTranslation(['priceDetails', 'common'])
  const language = useGetLanguage()

  // Local state
  const [isLoading, setIsLoading] = useState<boolean>(false)

  // Constants
  const electricityProduct = products ? getElectricityProduct(products, product) : undefined
  const gasProduct = products ? getGasProduct(products, product) : undefined
  const price = getSimulatedPrice(product)
  const productName = getProductName(product, language)

  return (
    <Drawer {...{ isOpen, onRequestClose }} className={styles.drawer}>
      {price ? (
        <div className={styles['price-drawer']}>
          <div className={styles['price-wrapper']}>
            {!!productName && (
              <Heading as="h2" variant="h5" className="mb-200">
                {productName}
              </Heading>
            )}
            <PriceDisplay
              className={styles.price}
              proposition={getProposition(product)}
              label={`${t('common:perMonth')} (${isCompany ? t('common:exclVat') : t('common:inclVat')})`}
              twoLines
            />
          </div>

          <Accordion items={[{ title: t('instalmentCalculationQuestion'), children: t('instalmentCalculationAnswer') }]} />

          <div className={styles['price-details-wrapper']}>
            <section className={styles['price-details']}>
              <header className={styles['price-details-header']}>
                <Icon name="electricity" className={styles.electricity} />
                <h3>{t('costRatioEl')}</h3>
                <data value={price.electricity[isCompany ? 'totalWithoutVAT' : 'total']}>
                  {formatPrice(price.electricity[isCompany ? 'totalWithoutVAT' : 'total'], language)}
                </data>

                {!!product && propositions?.simulatedPropositions?.[product]?.minimalInstalmentUsed?.electricity && (
                  <InlineMessage type="warning" className={styles['min-instalment-warning']}>
                    {t('minimumInstalmentWarning')}
                  </InlineMessage>
                )}
              </header>

              <div className={styles['price-details-content']}>
                <ul className={styles['price-details-table']}>
                  <li className={styles['price-details-row']}>
                    <span>{t('electricityBy', { producer: producer?.name[language] })}</span>
                    <data value={price.electricity.totalEnergy}>{formatPrice(price.electricity.totalEnergy, language)}</data>
                  </li>

                  {price.electricity.totalInjection && (
                    <li className={styles['price-details-row']}>
                      <span>{t('injection')}</span>
                      <data value={price.electricity.totalInjection}>{formatPrice(price.electricity.totalInjection, language)}</data>
                    </li>
                  )}

                  <li className={styles['price-details-row']}>
                    <span>{t('breakdownTransportAndTaxes')}</span>
                    <data value={price.electricity.transport}>{formatPrice(price.electricity.transport, language)}</data>
                  </li>

                  <li className={styles['price-details-row']}>
                    <span>{t('platformCosts')}</span>
                    <data value={price.electricity.subscription}>{formatPrice(price.electricity.subscription, language)}</data>
                  </li>

                  <li className={styles['price-details-row']}>
                    <span>{t('common:vat')}</span>
                    <data value={price.electricity.vat}>{formatPrice(price.electricity.vat, language)}</data>
                  </li>
                </ul>

                {!!products && (
                  <Link
                    className={styles['price-list-button']}
                    external
                    href={`https://${getPricelistFromElectricityProducts(
                      (electricityProduct?.details.code as ELProduct) ?? EL_PRODUCT_CODES.VariableOn,
                      products,
                      isCompany,
                      language
                    )}?time=${dayjs().unix()}`}
                    target="_blank"
                    variant="secondary"
                  >
                    {t('viewPricelistEl')}
                  </Link>
                )}
              </div>
            </section>

            {needsGas && price.gas && (
              <section className={styles['price-details']}>
                <header className={styles['price-details-header']}>
                  <Icon name="gas" className={styles.gas} />
                  <h3>{t('costRatioGas')}</h3>
                  <data value={price.gas[isCompany ? 'totalWithoutVAT' : 'total']}>
                    {formatPrice(price.gas[isCompany ? 'totalWithoutVAT' : 'total'], language)}
                  </data>

                  {!!product && propositions?.simulatedPropositions?.[product]?.minimalInstalmentUsed?.gas && (
                    <InlineMessage type="warning" className={styles['min-instalment-warning']}>
                      {t('minimumInstalmentWarning')}
                    </InlineMessage>
                  )}
                </header>

                <div className={styles['price-details-content']}>
                  <ul className={styles['price-details-table']}>
                    <li className={styles['price-details-row']}>
                      <span>{t('gasBy')}</span>
                      <data value={price.gas.totalEnergy}>{formatPrice(price.gas.totalEnergy, language)}</data>
                    </li>

                    <li className={styles['price-details-row']}>
                      <span>{t('breakdownTransportAndTaxes')}</span>
                      <data value={price.gas.transport}>{formatPrice(price.gas.transport, language)}</data>
                    </li>

                    <li className={styles['price-details-row']}>
                      <span>{t('platformCosts')}</span>
                      <data value={price.gas.subscription}>{formatPrice(price.gas.subscription, language)}</data>
                    </li>

                    <li className={styles['price-details-row']}>
                      <span>{t('common:vat')}</span>
                      <data value={price.gas.vat}>{formatPrice(price.gas.vat, language)}</data>
                    </li>
                  </ul>

                  {!!products && (
                    <Link
                      external
                      className={classNames(styles['price-list-button'], styles.gas)}
                      href={`https://${getPricelistFromGasProducts(
                        (gasProduct?.details.code as NGProduct) ?? GAS_PRODUCT_CODES.VariableOn,
                        products,
                        isCompany,
                        language
                      )}?time=${dayjs().unix()}`}
                      target="_blank"
                      variant="secondary"
                    >
                      {t('viewPricelistGas')}
                    </Link>
                  )}
                </div>
              </section>
            )}
          </div>

          {isSimulationPage && (
            <>
              <Button
                onClick={async () => {
                  dispatch(
                    selectProposition({
                      productType: (product || selectedProposition)!
                    })
                  )

                  // Apply promocode if necessary
                  if (product && checkCanApplyPlentyPromo(product)) {
                    setIsLoading(true)
                    const currentPromo = getProductPromoForDate(product)

                    if (currentPromo) {
                      const { valid } = await setPromoOrReferralCodeInStore(
                        isCompany ? currentPromo.code[CUSTOMER_TYPES.professional] : currentPromo.code[CUSTOMER_TYPES.residential]
                      )
                      if (valid) {
                        // request and save new propositions with the promocode. Don't await it, we don't need to wait for it
                        requestAndSavePropositions()
                      }
                    }
                  }

                  router.push(routes(language, selectedProducer).registration)
                  setIsLoading(false)
                }}
                className="mt-auto"
                isFullwidth
                loading={isLoading}
              >
                {t('common:switchNow')}
              </Button>

              {!!product && (
                <Button variant="layered" isFullwidth onClick={() => handleContactMeClick(product, onRequestClose)}>
                  {t('common:sendProposal')}
                </Button>
              )}
            </>
          )}
        </div>
      ) : // TODO: loading state?
      null}
    </Drawer>
  )
}

export default PriceDrawer
